import { inject, Injectable, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { filter, ReplaySubject, Subject } from 'rxjs';
import { APP_NAME } from '../app-info';
import { DEVICE_STATUS } from '../class/Device';
import {
  cloneDefaultPreference,
  ConfigPreference,
  DashboardCard,
  DefaultPreferences,
  DevicePreference,
  HistoryColumnsPreference,
  MapPreference,
  MissionPreference,
  PreferenceData,
  Preferences,
  SchedulePreference,
  TableColumnsPreference,
  TripTablePreference,
} from '../class/Preference';
import { AccountStateService } from './account-state.service';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { BaseService } from './base.service';
import { DeviceType } from './devices-manager.service';
import { EditService } from './edit.service';
import { LocalStorageService } from './local-storage.service';
import { SnackBarService } from './snack-bar.service';
import { TripstatsStoreService } from './tripstats-store.service';
@Injectable({
  providedIn: 'root',
})
export class PreferenceService extends BaseService<PreferenceData | number> {
  public preferencesLoadedEvent$ = new ReplaySubject<void>(1);
  private _localStorageService = inject(LocalStorageService);
  private _authService = inject(AuthService);
  private _accountStateService = inject(AccountStateService);
  private editService = inject(EditService);
  private snackBarService = inject(SnackBarService);
  private translateService = inject(TranslateService);
  /**
   * Émet le `type` de préférence dont l'enregistrement a réellement échoué (erreur réseau ou
   * refus du backend), pour que l'appelant puisse en avertir l'utilisateur avec un message qui
   * lui parle. N'émet pas quand la sauvegarde est volontairement ignorée parce que le site
   * consulté n'est pas celui du compte : voir `saveDraggedPreferences`.
   */
  public saveFailed$ = new Subject<string>();
  // Dependency-free store, safe to inject directly - see TripstatsStoreService.
  private tripstatsStore = inject(TripstatsStoreService);
  public componentDestroy$ = new Subject<void>();
  // equal: () => false forces every set()/update() to notify, since Preferences is mutated
  // in place (nested arrays/objects) rather than replaced - see mutatePreference().
  public $preference = signal<Preferences | null>(null, { equal: () => false });
  public preferencesLoaded$ = toObservable(this.$preference).pipe(filter(Boolean));
  public $mapPreferences = signal<MapPreference>(cloneDefaultPreference('map'));
  public preferenceId = 0;
  private preferenceVersion = '1.0';

  public $currentLang = signal<string>(
    this.translateService.currentLang() ?? this.translateService.fallbackLang() ?? 'fr'
  );

  constructor() {
    super(inject(ApiService));
  }

  public override HandleResponse(response: JSON): PreferenceData | number {
    if (response?.['read']) {
      const preference = new PreferenceData(response['read']['data'][0]);

      if (preference?.preference) {
        this.$preference.set(new Preferences(preference.preference));
        this.$mapPreferences.set(new MapPreference(this.$preference().map));
        this.preferenceId = preference.uid;
      } else {
        // `new Preferences()` sans données : chaque champ reçoit une copie des valeurs par
        // défaut. Affecter `DefaultPreferences` directement reviendrait à faire écrire ce compte
        // dans l'objet de module (voir `cloneDefaultPreference`).
        this.$preference.set(new Preferences());
        this.preferenceId = undefined;
      }
      return preference;
    } else if (response['delete']) {
      return response['delete']?.['affectedRows'];
    }
  }

  public loadPreference() {
    const storedPreference = LocalStorageService.getElement('preferences')?.preferences;
    if (storedPreference?.preference) {
      this.$preference.set(new Preferences(storedPreference.preference as Preferences));
      this.$mapPreferences.set(new MapPreference(this.$preference().map));
      this.preferenceId = storedPreference.uid;
      this.preferencesLoadedEvent$.next();
      this.translateService.use(
        this.$preference()?.config?.language === 'en' || this.$preference()?.config?.language === 'fr'
          ? this.$preference()?.config?.language
          : DefaultPreferences.config.language
      );
    } else {
      let preferenceData: PreferenceData;
      this.$preference.set(undefined);
      this.Get('preference', {
        action: 'read',
        token: this._authService.$token(),
        app: APP_NAME + ' - Preference ' + this.preferenceVersion,
      }).subscribe(
        value => {
          preferenceData = value as PreferenceData;
          this.translateService.use(
            preferenceData.preference?.config?.language || DefaultPreferences.config.language
          );
        },
        error => {
          console.log(error);
        },
        () => {
          this.updateStorage();
          this.preferencesLoadedEvent$.next();
        }
      );
    }
  }

  public savePreference(type: string, toaster?: boolean, deleteTripStat?: boolean) {
    if (deleteTripStat === undefined) {
      deleteTripStat = true;
    }
    let id: number;

    const form_data = new FormData();
    const params = {
      token: this._authService.$token(),
      app: APP_NAME + ' - Preference ' + this.preferenceVersion,
    };
    params['action'] = this.preferenceId ? 'update' : 'create';
    if (this.preferenceId) {
      params['uid'] = this.preferenceId;
    } else {
      params['uid_responsable'] = this._accountStateService.$account()?.uid;
    }
    for (const key in params) {
      form_data.append(key, params[key]);
    }
    form_data.append('preference', JSON.stringify(this.$preference()));

    this.editService.Post('preference', params, form_data).subscribe(
      response => (id = response),
      error => {
        console.log(error);
        this.saveFailed$.next(type);
      },
      () => {
        if (params['action'] === 'create') {
          this.preferenceId = id;
        }
        if (id !== -1) {
          console.log('Updated preferences successfully!');
          if (type === 'dashboard' && deleteTripStat) {
            LocalStorageService.removeElement('tripstats');
            this.tripstatsStore.$tripstats.set({});
          }
        } else {
          console.log('No success on update request!');
          this.saveFailed$.next(type);
          if (toaster && !this._accountStateService.$account()?.supervisor) {
            this.snackBarService.send({
              message: 'SNACK_BAR.FAILED_OP',
              type: 'error',
            });
          }
          if (this._accountStateService.$account()?.supervisor) {
            // we bluff the system
            LocalStorageService.removeElement('tripstats');
            this.tripstatsStore.$tripstats.set({});
          }
        }
      }
    );
  }

  /**
   * Mutates the current preference in place (nested arrays/objects) and re-notifies signal
   * consumers. Preferences is a mutable object held by a signal with `equal: () => false`,
   * so a direct `this.$preference().foo = x` never triggers change detection or effects -
   * always go through this method (or `.update()`/`.set()`) instead.
   */
  public mutatePreference(mutator: (preference: Preferences) => void): void {
    const preference = this.$preference();
    if (!preference) return;
    mutator(preference);
    this.$preference.set(preference);
  }

  public resetDashboardPreference() {
    this.mutatePreference(preference => {
      preference.dashboard = cloneDefaultPreference('dashboard') as DashboardCard[];
    });
    this.saveDraggedPreferences('dashboard', true);
  }

  //FOR CHART
  public destroyChart(index: number) {
    this.mutatePreference(preference => {
      preference.dashboard.splice(index, 1);
    });
    this.tripstatsStore.deleteAt(index);
    this._localStorageService.storeElement('tripstats', {
      stats: this.tripstatsStore.$tripstats(),
    });
    this.saveDraggedPreferences('dashboard', true);
  }

  public updateStorage() {
    const datas = {
      preference: this.$preference(),
      uid: this.preferenceId,
    };
    this._localStorageService.storeElement('preferences', { preferences: datas });
  }

  public saveDraggedPreferences(type: string, toaster: boolean, deleteTripStat?: boolean) {
    // Function to call on header's button
    const company = this._accountStateService.$company();
    if (!company || company.uid === this._accountStateService.$account().uid_site) {
      if (deleteTripStat === undefined) deleteTripStat = true;
      this.savePreference(type, toaster, deleteTripStat);
    } else {
      if (toaster) {
        this.snackBarService.send({
          message: 'SNACK_BAR.WRONG_SITE',
          type: 'error',
        });
      }
    }
    this.updateStorage();
  }

  public setPreferences(
    type: string,
    datas:
      | MapPreference
      | SchedulePreference
      | TripTablePreference
      | MissionPreference
      | ConfigPreference
      | DevicePreference
      | HistoryColumnsPreference
      | TableColumnsPreference
  ) {
    let change = false;
    const preference = this.$preference();
    if (preference) {
      if (Array.isArray(datas)) {
        if (JSON.stringify(preference[type]) !== JSON.stringify(datas)) {
          preference[type] = datas;
          change = true;
        }
      } else if (preference[type] !== datas && preference[type]) {
        for (const item in datas) {
          if (datas[item] !== preference[type][item] && datas[item] != undefined) {
            preference[type][item] = datas[item];
            this.$mapPreferences.set(new MapPreference(preference.map));
            change = true;
          }
        }
      } else if (DefaultPreferences[type] !== datas && !preference[type]) {
        preference[type] = datas;
        this.$mapPreferences.set(new MapPreference(preference.map));
      }
      this.$preference.set(preference);
      if (change) this.saveDraggedPreferences(type, false);
    }
  }

  public getUnitsFilter() {
    // "all" mean we want to see all units, "none" mean we want see nothing
    const devicesFilterPreferences = this.$mapPreferences()?.deviceFilter || 'all';

    if (devicesFilterPreferences === 'all' || devicesFilterPreferences === 'none') {
      return [];
    }

    return devicesFilterPreferences.split(',')?.map(Number);
  }

  public getMovingStatusFilter() {
    const deviceMovingStatusFilter = this.$mapPreferences().deviceMovingStatusFilter;
    if (deviceMovingStatusFilter && typeof deviceMovingStatusFilter === 'string') {
      return deviceMovingStatusFilter.split(',') as (keyof typeof DEVICE_STATUS)[];
    }
    return [];
  }

  public getDeviceTypeFilter() {
    const deviceTypeFilter = this.$mapPreferences().deviceTypeFilter;
    if (deviceTypeFilter && typeof deviceTypeFilter === 'string') {
      return deviceTypeFilter.split(',') as DeviceType[];
    }
    return [];
  }

  public getLandmarksFilter() {
    // "all" mean we want to see all units, "none" mean we want see nothing
    const filter = this.$preference()?.map?.poiFilter || 'all';

    if (filter === 'all' || filter === 'none') return [];

    return filter.split(',').map(Number);
  }

  /**
   * Drop the preferences held by this service so the next `loadPreference()` starts from scratch.
   *
   * Local state only — unlike `resetPreferences()`, nothing is deleted server-side. Called when the
   * session is torn down (logout, account switch): `$mapPreferences` in particular feeds
   * `getUnitsFilter()`, so keeping the previous account's value there filters the new fleet's
   * devices down to none.
   */
  public clear() {
    this.$preference.set(null);
    // Une copie, pas la référence du module : `$mapPreferences` est ensuite écrit en place
    // (`setPreferences`), et affecter `DefaultPreferences.map` directement ferait écrire le compte
    // suivant dans les valeurs par défaut de l'application (voir `cloneDefaultPreference`).
    this.$mapPreferences.set(new MapPreference(cloneDefaultPreference('map')));
    this.preferenceId = 0;
    this.preferencesLoadedEvent$ = new ReplaySubject<void>(1);
    this.$preference.set(null);
  }

  public resetPreferences() {
    const preferenceUid =
      this.preferenceId || LocalStorageService.getElement('preferences')?.preferences?.uid;
    const company = this._accountStateService.$company();
    if (preferenceUid && (!company || company.uid === this._accountStateService.$account().uid_site)) {
      const params = {
        action: 'delete',
        uid: this.preferenceId,
        token: this._authService.$token(),
      };
      let id: number;
      this.Get('preference', params).subscribe(
        response => (id = response as number),
        error => {
          console.log(error);
        },
        () => {
          console.log('delete preference', id);
        }
      );
    }
  }
}
